import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import Button from './Button'
import InfoHint from './InfoHint'
import useBodyScrollLock from './useBodyScrollLock'
import { kit } from './strings'
import './Drawer.css'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * The side sheet — the app's one overlay surface for anything with a form in it.
 *
 * Editing used to happen in a centred modal that covered the very list you were
 * editing from. This docks at the reading-start edge instead, so it opens where
 * the eye already is and leaves the list readable behind it. ConfirmDialog and
 * ResultDialog stay centred: those are the two cases where covering the page is
 * the point.
 *
 * @param {'sm'|'md'|'lg'|'xl'|'full'} [size]
 * @param {import('react').ElementType} [icon]  emblem glyph for the head
 * @param {string} [hint]  long-form guidance, folded behind an "i" beside the title
 * @param {import('react').ReactNode} [footer]  action strip pinned to the bottom
 */
export default function Drawer({
  open,
  /* Centred-and-high rather than docked at the edge — see .drawer--dialog. */
  dialog = false,
  title,
  subtitle,
  hint,
  icon: Icon,
  size = 'md',
  onClose,
  footer,
  children,
}) {
  const panelRef = useRef(null)
  const titleId = useId()

  // `onClose` is nearly always an inline arrow, so it must not sit in an
  // effect's deps — re-running the focus effect on every parent render would
  // pull the caret out of whatever field is being typed in.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useBodyScrollLock(open)

  useEffect(() => {
    if (!open) return undefined

    const previous = document.activeElement
    panelRef.current?.focus()

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseRef.current?.()
        return
      }
      if (event.key !== 'Tab') return

      // Keep Tab inside the sheet: it is modal, so the page behind it must not
      // be reachable by keyboard while it is open.
      const items = [...(panelRef.current?.querySelectorAll(FOCUSABLE) ?? [])]
      if (items.length === 0) return

      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || active === panelRef.current)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (previous instanceof HTMLElement) previous.focus()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className={`drawer${dialog ? ' drawer--dialog' : ''}`}
      role="presentation"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className={`drawer__panel drawer__panel--${size}`}
        data-testid="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="drawer__head">
          {Icon && (
            <span className="drawer__emblem" aria-hidden="true">
              <Icon size={19} strokeWidth={1.75} />
            </span>
          )}

          <div className="drawer__titles">
            <h2 className="drawer__title" id={titleId}>
              <bdi className="drawer__title-text">{title}</bdi>
              {hint && <InfoHint content={hint} />}
            </h2>
            {subtitle && (
              <p className="drawer__subtitle">
                <bdi>{subtitle}</bdi>
              </p>
            )}
          </div>

          <button type="button" className="drawer__close" data-testid="drawer-close" onClick={onClose} aria-label={kit.close}>
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        <div className="drawer__body">{children}</div>

        {footer && <footer className="drawer__foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  )
}

/**
 * The same sheet wrapped in a form, with the submit/cancel pair in the footer —
 * the shape every edit screen in the panel needs.
 */
/**
 * A form in a dialog.
 *
 * Centred by default, not docked. These were side sheets throughout, and a form
 * pinned to one edge of a wide screen reads as attached to the browser rather
 * than to the page — you look for it where you left the row, and it is not
 * there. `dock` puts one back on the edge for the rare sheet that is genuinely
 * worked ALONGSIDE the list behind it.
 *
 * Anything with steps, or with more fields than fit two comfortable columns,
 * belongs on a FormPage instead.
 */
export function FormDrawer({ onSubmit, submitLabel, cancelLabel, submitting, dock = false, ...rest }) {
  return (
    <Drawer
      dialog={!dock}
      {...rest}
      footer={
        <>
          <Button type="submit" form={rest.formId} busy={submitting}>
            {submitLabel}
          </Button>
          <Button variant="ghost" onClick={rest.onClose}>
            {cancelLabel}
          </Button>
        </>
      }
    >
      <form id={rest.formId} onSubmit={onSubmit}>
        {rest.children}
      </form>
    </Drawer>
  )
}
